import { useEffect, useRef, useState, useCallback } from "react";
import { Bell, BellSimple, CheckCircle, Storefront } from "@phosphor-icons/react";
import API from "@/lib/api";
import { formatDistanceToNow } from "@/lib/timeago";

const POLL_INTERVAL_MS = 30000;

const iconFor = (type) => {
  switch (type) {
    case "brand_status":
      return Storefront;
    default:
      return BellSimple;
  }
};

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const fetchNotifs = useCallback(async () => {
    try {
      const { data } = await API.get("/publisher/notifications?limit=20");
      setItems(data.items || []);
      setUnreadCount(data.unread_count || 0);
    } catch {
      // silently ignore — not critical
    }
  }, []);

  useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [fetchNotifs]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleMarkOne = async (id) => {
    await API.post(`/publisher/notifications/${id}/read`);
    fetchNotifs();
  };

  const handleMarkAll = async () => {
    await API.post("/publisher/notifications/read-all");
    fetchNotifs();
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid="notification-bell-wrapper">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-md hover:bg-slate-100 transition-colors"
        aria-label="Notifications"
        data-testid="notification-bell-btn"
      >
        <Bell size={20} weight={unreadCount > 0 ? "fill" : "regular"} className="text-slate-700" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notification-unread-badge"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute top-full right-0 mt-2 w-[380px] bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <div>
              <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-slate-400">
                Inbox
              </p>
              <h3 className="font-heading text-base font-bold text-slate-900 tracking-tight">
                Notifications
              </h3>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAll}
                className="text-xs text-[#002FA7] font-medium hover:underline"
                data-testid="mark-all-read-btn"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <BellSimple size={28} className="text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">You're all caught up.</p>
              </div>
            ) : (
              items.map((n) => {
                const Icon = iconFor(n.type);
                return (
                  <div
                    key={n.id}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors ${
                      !n.read ? "bg-blue-50/40" : ""
                    }`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        !n.read ? "bg-[#002FA7] text-white" : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      <Icon size={16} weight="bold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 leading-snug">
                        {n.title}
                      </p>
                      <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{n.body}</p>
                      <p className="text-[11px] text-slate-400 mt-1">
                        {formatDistanceToNow(n.created_at)}
                      </p>
                    </div>
                    {!n.read && (
                      <button
                        onClick={() => handleMarkOne(n.id)}
                        className="text-slate-400 hover:text-[#002FA7] shrink-0"
                        aria-label="Mark as read"
                        data-testid={`mark-read-${n.id}`}
                      >
                        <CheckCircle size={18} />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
